import { DispatchCard } from 'src/entities/dispatch';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';

import { buildOverloadFlags } from './vehicleLoad';

/** 判定に使う勤務の最小情報。V3Plans (PlanningsDelivery.presenter.tsx) と同じ項目 */
export type ConstraintDelivery = Pick<
  PlanningsDeliveryWithLegRunEntity,
  'startAt' | 'endAt' | 'operations' | 'workingAvailableDurationSeconds'
> & { workStartAt?: string | null; workEndAt?: string | null };

const toTime = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const time = new Date(iso).getTime();
  return Number.isNaN(time) ? null : time;
};

/**
 * 実際に働いた区間。work_start_at / work_end_at があればそれ、無ければ operation の
 * 最初の到着〜最後の出発（V3Plans PlanningsDelivery.presenter.tsx:309-324 と同じ）。
 */
function workingRange(delivery: ConstraintDelivery): { start: number; end: number } | null {
  const operations = delivery.operations ?? [];
  const arrivals = operations.map((op) => toTime(op.arrivalAt)).filter((t): t is number => t != null);
  const departures = operations.map((op) => toTime(op.departureAt)).filter((t): t is number => t != null);

  const start = toTime(delivery.workStartAt) ?? (arrivals.length > 0 ? Math.min(...arrivals) : null);
  const end = toTime(delivery.workEndAt) ?? (departures.length > 0 ? Math.max(...departures) : null);
  if (start == null || end == null) return null;
  return { start, end };
}

/**
 * 勤務時間の超過。V3Plans (PlanningsDelivery.presenter.tsx:236-258) と同じ判定。
 *
 * 1. 労働可能時間 (workingAvailableDurationSeconds) を、待機を除いた実働が超える
 * 2. 勤務の開始前に動き出している、または終了後まで動いている
 *
 * 配車が 1 件も無ければ超過しようがない。
 */
export function isOvertime(delivery: ConstraintDelivery): boolean {
  const operations = delivery.operations ?? [];
  if (operations.length === 0) return false;

  const range = workingRange(delivery);
  if (!range) return false;

  // null_duration_seconds は「勤務の谷間」。実働から除く（V3Plans と同じ）
  const nullSeconds = operations
    .map((op) => op.nullDurationSeconds ?? 0)
    .filter((seconds) => seconds > 0)
    .reduce((total, seconds) => total + seconds, 0);
  const workedSeconds = (range.end - range.start) / 1000 - nullSeconds;
  const available = delivery.workingAvailableDurationSeconds;
  if (available && workedSeconds > available) return true;

  const shiftStart = toTime(delivery.startAt);
  const shiftEnd = toTime(delivery.endAt);
  if (shiftStart != null && range.start < shiftStart) return true;
  return shiftEnd != null && shiftEnd < range.end;
}

/**
 * 積載以外の制約違反。V3Plans が車両カードの Tooltip に並べるものを揃える
 * (PlanningsDelivery.presenter.tsx:269-275)。文言も並び順もそのまま合わせる。
 *
 * 勤務時間は勤務そのもの、指定時間とバース予約は地点（カード）ごとの判定なので、
 * その勤務のカードを 1 枚でも違反していれば勤務の違反として出す。
 */
export function buildConstraintAlerts(
  delivery: ConstraintDelivery | undefined,
  cards: readonly Pick<DispatchCard, 'hasAlert' | 'hasBerthAlert'>[],
): string[] {
  const alerts: string[] = [];
  if (delivery && isOvertime(delivery)) alerts.push('勤務時間が超過しています。');
  if (cards.some((card) => card.hasAlert)) alerts.push('指定時間が超過しています。');
  if (cards.some((card) => card.hasBerthAlert)) alerts.push('バース予約の時間外です。');
  return alerts;
}

/**
 * 回転行ごとの制約違反。キーは `RotationRow.rowId`。
 *
 * 指定時間とバース予約は地点（カード）ごとの判定なので、**そのカードが乗っている回転の行**に
 * 出す。勤務単位にまとめると 2 回転目の違反が 1 回転目のカードに並び、どの回転が悪いのか
 * 読めない (log_web_issues#297)。
 *
 * 勤務時間の超過だけは回転ではなく勤務そのものの違反なので、先頭の回転にだけ出す
 * （全行に出すと同じ違反が回転の数だけ並ぶ。粗利など「勤務全体の値」を先頭行に置くのと
 * 同じ扱い）。
 *
 * 固定ルート由来の合成行しか無いグループ（勤務が無い）は従来どおり対象外。
 */
export function buildConstraintAlertsByRowId(
  rowGroups: readonly VehicleRowGroup[],
  deliveries: readonly (ConstraintDelivery & { id: number })[],
): Map<string, string[]> {
  const deliveryById = new Map(deliveries.map((d) => [d.id, d]));
  const result = new Map<string, string[]>();
  rowGroups.forEach((group) => {
    const { shiftTruckId } = group;
    if (shiftTruckId == null) return;
    const delivery = deliveryById.get(shiftTruckId);
    group.rows.forEach((row, index) => {
      // delivery を渡した行だけ勤務時間の超過を見る（buildConstraintAlerts の第 1 引数）
      const alerts = buildConstraintAlerts(index === 0 ? delivery : undefined, row.cards);
      if (alerts.length > 0) result.set(row.rowId, alerts);
    });
  });
  return result;
}

/**
 * この車両（勤務）に制約違反があるか。「制約違反のある車両のみ」の絞り込みで使う。
 *
 * 判定はカード右上の警告バッジ（buildVehicleAlert）と同じ組み合わせにする。
 * バッジと食い違うと、絞り込んだ結果に警告の無い車両が混ざる。
 * 積載も制約違反も回転ごとなので、行のどれかが超えていれば車両の違反として扱う。
 */
export function hasVehicleConstraintViolation(
  group: VehicleRowGroup,
  constraintAlertsByRowId?: ReadonlyMap<string, readonly string[]>,
): boolean {
  // 違反は回転行ごとに持つので、どれかの行に付いていれば車両の違反として扱う
  if (group.rows.some((row) => (constraintAlertsByRowId?.get(row.rowId)?.length ?? 0) > 0)) return true;
  return group.rows.some((row) => {
    const flags = buildOverloadFlags(group.vehicle, row.cards);
    return flags.quantity || flags.weight || flags.volume;
  });
}
